import { useMemo } from 'react';
import { useComponentBaseCosts } from './useComponentBaseCosts';
import { FilterRule } from '@/types/adjustment-filters';
import { evaluateFilterRules } from '@/utils/adjustmentFilterUtils';

interface CostAnalysisItem {
  id: string;
  description: string;
  quantity: number;
  unitBaseCost: number;
  unitFinalCost: number;
  totalBaseCost: number;
  totalFinalCost: number;
  category: string;
  componentType: 'supplier_line_item' | 'internal_line_item' | 'assembly' | 'custom';
  markup: number;
  margin: number;
}

interface CategorySummary {
  categoryName: string;
  totalBaseCost: number;
  totalFinalCost: number;
  itemCount: number;
  averageMarkup: number;
  averageMargin: number;
}

interface CostAnalysisData {
  items: CostAnalysisItem[];
  categorySummaries: CategorySummary[];
  totalBaseCost: number;
  totalFinalCost: number;
  overallMarkup: number;
  overallMargin: number;
  discountPercentage: number;
  discountAmount: number;
  totalFinalCostAfterDiscount: number;
  overallMarkupAfterDiscount: number;
  overallMarginAfterDiscount: number;
}

export const useInMemoryCostAnalysis = (lineItems: any[] | undefined, discountPercentage?: number, adjustmentFilters?: FilterRule[]): CostAnalysisData | null => {
  const { data: baseCostsMap, isLoading } = useComponentBaseCosts(lineItems);

  return useMemo(() => {
    if (!lineItems || lineItems.length === 0) {
      return null;
    }

    // Wait for base costs to load
    if (isLoading || !baseCostsMap) {
      return null;
    }

    const items: CostAnalysisItem[] = [];
    let runningTotalBaseCost = 0;
    let runningTotalFinalCost = 0;

    // Process each line item
    lineItems.forEach((item) => {
      const quantity = item.quantity_in_base_units || item.quantity || 0;
      
      // Extract unit base cost from fetched component data
      let unitBaseCost = 0;
      let categoryName = 'Uncategorized';

      // Handle custom items differently - they store base cost in manual_unit_cost
      if (item.component_type === 'custom') {
        unitBaseCost = item.manual_unit_cost || 0;
        // For custom items, get category name from joined supplier_categories
        categoryName = item.supplier_categories?.name || 'Custom';
      } else if (item.component_type && item.component_id && baseCostsMap[item.component_type]) {
        const componentData = baseCostsMap[item.component_type][item.component_id];
        if (componentData) {
          unitBaseCost = componentData.base_cost || componentData.total_base_cost || 0;
          categoryName = componentData.category_name || 'Uncategorized';
        }
      }

      // Do NOT fallback to snapshot - it contains final customer price, not base cost
      // If base cost is missing, it indicates a data issue that should be visible (except for custom items)
      if (unitBaseCost === 0 && item.component_type !== 'custom') {
        console.warn(`[useInMemoryCostAnalysis] Missing base cost for ${item.component_type}:${item.component_id}`);
      }

      // Get unit final cost
      let unitFinalCost: number;
      if (item.component_type === 'custom') {
        unitFinalCost = item.unit_cost_snapshot || 0;
      } else if (item.manual_override && item.manual_unit_cost !== null) {
        unitFinalCost = item.manual_unit_cost;
      } else {
        unitFinalCost = item.unit_cost_snapshot || 0;
      }

      // Apply markup removal filters: if item matches, use base cost instead of marked-up price
      const effectiveFilters = Array.isArray(adjustmentFilters) ? adjustmentFilters : [];
      if (effectiveFilters.length > 0) {
        const matchesFilter = evaluateFilterRules(effectiveFilters, {
          category_id: item.category_id || undefined,
          trade_id: undefined, // trade_id not directly available on line items
          quantity_in_base_units: quantity,
          base_cost: unitBaseCost,
        });

        if (matchesFilter) {
          unitFinalCost = unitBaseCost;
        }
      }

      // Calculate totals
      const totalBaseCost = unitBaseCost * quantity;
      const totalFinalCost = unitFinalCost * quantity;

      runningTotalBaseCost += totalBaseCost;
      runningTotalFinalCost += totalFinalCost;

      // Calculate markup and margin (using unit costs)
      const markup = unitBaseCost > 0 ? ((unitFinalCost - unitBaseCost) / unitBaseCost) * 100 : 0;
      const margin = unitFinalCost > 0 ? ((unitFinalCost - unitBaseCost) / unitFinalCost) * 100 : 0;

      items.push({
        id: item.id,
        description: item.description || 'Unnamed Item',
        quantity,
        unitBaseCost,
        unitFinalCost,
        totalBaseCost,
        totalFinalCost,
        category: categoryName,
        componentType: item.component_type || 'custom',
        markup,
        margin
      });
    });

    // Calculate overall metrics
    const overallMarkup = runningTotalBaseCost > 0 ? ((runningTotalFinalCost - runningTotalBaseCost) / runningTotalBaseCost) * 100 : 0;
    const overallMargin = runningTotalFinalCost > 0 ? ((runningTotalFinalCost - runningTotalBaseCost) / runningTotalFinalCost) * 100 : 0;

    // Group by category and calculate category summaries
    const categoryMap = new Map<string, CostAnalysisItem[]>();
    items.forEach(item => {
      const categoryItems = categoryMap.get(item.category) || [];
      categoryItems.push(item);
      categoryMap.set(item.category, categoryItems);
    });

    const categorySummaries: CategorySummary[] = Array.from(categoryMap.entries()).map(([categoryName, categoryItems]) => {
      const totalBaseCost = categoryItems.reduce((sum, item) => sum + item.totalBaseCost, 0);
      const totalFinalCost = categoryItems.reduce((sum, item) => sum + item.totalFinalCost, 0);
      const itemCount = categoryItems.length;
      
      const averageMarkup = totalBaseCost > 0 ? ((totalFinalCost - totalBaseCost) / totalBaseCost) * 100 : 0;
      const averageMargin = totalFinalCost > 0 ? ((totalFinalCost - totalBaseCost) / totalFinalCost) * 100 : 0;

      return {
        categoryName,
        totalBaseCost,
        totalFinalCost,
        itemCount,
        averageMarkup,
        averageMargin
      };
    });

    // Calculate discount metrics
    const effectiveDiscount = (typeof discountPercentage === 'number' && discountPercentage > 0) ? discountPercentage : 0;
    const discountAmount = runningTotalFinalCost * (effectiveDiscount / 100);
    const totalFinalCostAfterDiscount = runningTotalFinalCost - discountAmount;
    const overallMarkupAfterDiscount = runningTotalBaseCost > 0 ? ((totalFinalCostAfterDiscount - runningTotalBaseCost) / runningTotalBaseCost) * 100 : 0;
    const overallMarginAfterDiscount = totalFinalCostAfterDiscount > 0 ? ((totalFinalCostAfterDiscount - runningTotalBaseCost) / totalFinalCostAfterDiscount) * 100 : 0;

    return {
      items,
      categorySummaries,
      totalBaseCost: runningTotalBaseCost,
      totalFinalCost: runningTotalFinalCost,
      overallMarkup,
      overallMargin,
      discountPercentage: effectiveDiscount,
      discountAmount,
      totalFinalCostAfterDiscount,
      overallMarkupAfterDiscount,
      overallMarginAfterDiscount
    };
  }, [lineItems, baseCostsMap, isLoading, discountPercentage, adjustmentFilters]);
};
